import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import {
  Box, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { DataGrid } from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import { getLevelColor, getSeriesColors } from 'src/components/BpoMutabakat/colors';
import { reportName, reportShort } from 'src/components/BpoMutabakat/reports';
import {
  formatDecimal, formatLongDuration, formatNumber, formatPercent, formatShortDate, formatTime
} from 'src/components/BpoMutabakat/format';

/** Her rapor tipinin ekranda karşılık geldiği blok. */
export const SCREEN_LOCATION = {
  1: 'Genel Bakış · Kuyruk özeti',
  2: 'Kalite · Puan dağılımı',
  3: 'Kuyruk & Skill · Kuyruk',
  4: 'Kuyruk & Skill · Departman',
  5: 'Kuyruk & Skill · Özel grup',
  6: 'Agent · Performans tablosu',
  7: 'Outbound · Dialer / manuel',
  8: 'Outbound · Arama sonucu',
  9: 'Outbound · Yurtdışı',
  10: 'Outbound · Skill bazında',
  11: 'Outbound · Agent bazında',
  12: 'Çağrı Detayı · Aranan numara',
  13: 'Çağrı Detayı · Çağrı kayıtları',
  14: 'Genel Bakış · Kanal donutu',
  15: 'Genel Bakış · Interval grafiği',
  16: 'Agent · Gün içi kapasite',
  18: 'Kalite & Ipsos · Skor kartları'
};

const DataHealth = ({ summary, intervals, reports, lastSevenDays, loading }) => {
  const theme = useTheme();
  const colors = getSeriesColors(theme);
  const statusColors = {
    loaded: { ...getLevelColor(theme, 'good'), label: 'yüklendi' },
    missing: { ...getLevelColor(theme, 'bad'), label: 'yüklenmedi' },
    monthly: { ...getLevelColor(theme, 'empty'), label: 'aylık · dönem' }
  };

  /* RT 15'in kendi toplamları — RT 1 ile farkı burada görünür olsun diye. */
  const intervalTotals = useMemo(() => {
    const slots = intervals || [];
    const incoming = slots.reduce((a, r) => a + r.incoming, 0);
    const answered = slots.reduce((a, r) => a + r.answered, 0);
    let weight = 0;
    let volume = 0;
    slots.forEach((r) => {
      if (r.sl !== null && r.sl !== undefined) {
        weight += r.sl * r.incoming;
        volume += r.incoming;
      }
    });
    return { incoming, answered, sl: volume ? weight / volume : null };
  }, [intervals]);

  const columns = [
    { field: 'rt', headerName: 'No', type: 'number', width: 62 },
    { field: 'name', headerName: 'Rapor', flex: 1.5, minWidth: 230 },
    {
      field: 'table',
      headerName: 'Tablo',
      flex: 1.4,
      minWidth: 240,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'rows',
      headerName: 'Satır',
      type: 'number',
      width: 96,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'writeSec',
      headerName: 'Yazma',
      type: 'number',
      width: 96,
      valueFormatter: (v) =>
        v === null || v === undefined ? '—' : `${formatDecimal(v, 0)} sn`
    },
    {
      field: 'msPerRow',
      headerName: 'Satır başı',
      type: 'number',
      width: 110,
      valueGetter: (value, row) =>
        row.writeSec === null || !row.rows ? null : (row.writeSec / row.rows) * 1000,
      valueFormatter: (v) => (v === null ? '—' : `${formatDecimal(v)} ms`)
    },
    {
      field: 'lastWrite',
      headerName: 'Son yazım',
      width: 110,
      valueFormatter: (v) => formatTime(v)
    },
    {
      field: 'screen',
      headerName: 'Ekranda',
      flex: 1.2,
      minWidth: 200,
      valueGetter: (value, row) => SCREEN_LOCATION[row.rt] || '—',
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Durum',
      width: 160,
      renderCell: (params) => {
        const color = statusColors[params.value] || statusColors.missing;
        return (
          <Box
            component="span"
            sx={{
              px: 1,
              py: '2px',
              borderRadius: 5,
              fontSize: '0.72rem',
              fontWeight: 500,
              color: color.text,
              backgroundColor: color.bg
            }}
          >
            {color.label}
          </Box>
        );
      }
    }
  ];

  const daily = (reports || []).filter((r) => !r.monthly);
  const loaded = daily.filter((r) => r.status === 'loaded');
  const totalRows = daily.reduce((a, r) => a + (r.rows || 0), 0);
  const totalSec = (reports || []).reduce((a, r) => a + (r.writeSec || 0), 0);

  const dayChart = {
    series: [{ name: 'Toplam satır', data: (lastSevenDays || []).map((d) => d.rows) }],
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: theme.typography.fontFamily,
        background: 'transparent'
      },
      theme: { mode: theme.palette.mode },
      colors: [colors[0]],
      plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
      dataLabels: { enabled: false },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
      xaxis: {
        categories: (lastSevenDays || []).map((d) => formatShortDate(d.date)),
        axisTicks: { show: false }
      },
      yaxis: { labels: { formatter: (v) => formatNumber(Math.round(v)) } },
      tooltip: { theme: theme.palette.mode, y: { formatter: (v) => `${formatNumber(v)} satır` } }
    }
  };

  const consistency = [
    {
      /* Kapsam satırı verinin kendisinden türetilir; sabit yazılırsa başka bir
         gün için yanlış olur. */
      metric: 'Kapsam',
      channel: 'tüm santral',
      queue: summary?.queueCount ? `${formatNumber(summary.queueCount)} kuyruk` : '—',
      interval: intervals?.length ? `${formatNumber(intervals.length)} dilim` : '—',
      agent: summary?.agentCount ? `${formatNumber(summary.agentCount)} agent` : '—'
    },
    {
      metric: 'Gelen çağrı',
      channel: formatNumber(summary?.switchboardCalls),
      queue: formatNumber(summary?.queuedCalls),
      interval: formatNumber(intervalTotals.incoming),
      agent: '—'
    },
    {
      metric: 'Cevaplanan',
      channel: '—',
      queue: formatNumber(summary?.answered),
      interval: formatNumber(intervalTotals.answered),
      agent: formatNumber(summary?.agentAcdCalls)
    },
    {
      metric: 'Servis seviyesi 80/20',
      channel: '—',
      queue: formatPercent(summary?.serviceLevel),
      interval: formatPercent(intervalTotals.sl),
      agent: '—'
    }
  ];

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <SectionCard
          title="Yükleme sonucu"
          subtitle={`${loaded.length}/${daily.length} günlük rapor · ${formatNumber(totalRows)} satır · ${formatLongDuration(totalSec)} yazma`}
          padded={false}
        >
          <Box sx={{ height: 640, width: '100%' }}>
            <DataGrid
              rows={(reports || []).map((r) => ({ ...r, id: `${r.rt}-${r.table}` }))}
              columns={columns}
              loading={loading}
              density="compact"
              disableRowSelectionOnClick
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50]}
              sx={{ border: 0 }}
            />
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 7 }}>
        <SectionCard
          title="Raporlar arası tutarlılık"
          subtitle="aynı günün farklı ölçüleri"
          padded={false}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ölçü</TableCell>
                  <TableCell>{reportShort(14)}</TableCell>
                  <TableCell>{reportShort(1)}</TableCell>
                  <TableCell>{reportShort(15)}</TableCell>
                  <TableCell>{reportShort(6)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consistency.map((row) => (
                  <TableRow key={row.metric} hover>
                    <TableCell>{row.metric}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.channel}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.queue}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.interval}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.agent}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box sx={{ p: 2, display: 'grid', gap: 1.5 }}>
            <InfoNote>
              <b>Tutan sağlama.</b> {reportName(14)} raporunun kanal toplamı,
              {' '}{reportName(13)} raporunun satır sayısıyla birebir aynı. Özet ile detay
              rapor uyuşuyor.
            </InfoNote>
            <InfoNote>
              <b>Açıklanması gereken farklar.</b> {reportName(1)} ile {reportName(15)} aynı
              şeyi ölçüyor görünmesine rağmen farklı sonuç veriyor &mdash; ikisinin kapsamı
              farklı, yukarıdaki Kapsam satırı bunu gösteriyor. {reportName(6)} raporundaki
              ACD çağrı sayısı ise agent’ın devraldığı transferleri de sayıyor olabilir.
              <b> Faturalama hangi rapora dayanıyorsa mutabakat onun üzerinden yürümeli;</b>
              ekran üçünü de gösterip farkı görünür tutuyor.
            </InfoNote>
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 5 }}>
        <SectionCard title="Son 7 gün" subtitle="toplam satır">
          {loading ? (
            <Skeleton variant="rounded" height={230} />
          ) : (
            <ReactApexChart
              options={dayChart.options}
              series={dayChart.series}
              type="bar"
              height={230}
            />
          )}
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <SectionCard title="Ekranın okunması">
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <InfoNote>
              <b>Tekrar çalıştırma güvenlidir.</b> Her rapor, kendi satırlarını yazmadan önce o
              güne ait eski kayıtları <code>PR_BPO_MUTABAKAT_RAPOR_TEMIZLE</code> ile siler. Aynı
              gün ikinci kez çalıştırılırsa satır sayısı değişmez, veri ikilenmez.
            </InfoNote>
            <InfoNote>
              <b>Gün seçimi.</b> Job her gece bir gün geriden çalışır; gün içinde çağrı kaydı
              akmaya devam ettiği için aynı günü çekmek günün kalanını kaçırırdı.{' '}
              <code>PR_WALBOARD_TARIH_ARALIGI_BELIRLEME</code> işlenecek günü döndürür.
            </InfoNote>
            <InfoNote>
              <b>Toplama Ipsos dahil değildir.</b> Günlük toplam, her gece çekilen 16 raporun
              toplamıdır. {reportName(18)} aylık çalışır ve dönem başına satır yazar;
              <code>VERI_TARIHI</code> ayın ilk günüdür. Günlük toplamla karıştırılmaması için
              ayrı gösteriliyor.
            </InfoNote>
          </Box>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

DataHealth.propTypes = {
  summary: PropTypes.object,
  intervals: PropTypes.array,
  reports: PropTypes.array,
  lastSevenDays: PropTypes.array,
  loading: PropTypes.bool
};

export default DataHealth;
